import { getNextIncompleteModuleID } from "./helpers";
import type {
  Goal as SfccGoal,
  Level as SfccLevel,
  Module as SfccModule,
} from "./types";

// LMS Builder-content → view-model transformers, ported verbatim from composable
// `hec/builder/models/learning-center/{levels,module,goalBanner}.js`
// (SFCC-11503 / ARTICLES). PURE — combine resolved Builder entries with the
// SFCC progress data from `useLearningCenter`. Builder ref shapes vary by how
// they were resolved: levels inline as `.level.value.data`; modules resolve as
// `.module.data` (composable's manual enrich) — both tolerated via optional chains.

// --- Raw Builder shapes (loose; only the fields the transformers read) ---
export interface PageRef {
  value?: { data?: { url?: string } };
}
interface RawLesson {
  id?: string;
  title?: string;
  content?: unknown;
}
interface LessonRef {
  lesson?: { value?: { data?: RawLesson } };
}
interface RawQuizQuestion {
  question: string;
  answers: { answer: string; isCorrect?: boolean }[];
}
interface QuizRef {
  value?: { data?: { id?: string; questions?: RawQuizQuestion[] } };
}
interface RawModule {
  id?: string;
  title?: string;
  image?: string;
  estimatedCompletionTime?: number;
  lessons?: LessonRef[];
  quiz?: QuizRef;
  summary?: string;
  page?: PageRef;
}
/** Resolved module ref — `.data` (manual enrich) or `.value.data` (SDK enrich). */
interface ModuleRefWrap {
  module: { id?: string; data?: RawModule; value?: { data?: RawModule } };
}
interface RawLevel {
  id: string;
  title?: string;
  modules: ModuleRefWrap[];
  loyaltyActionId?: string | null;
}
interface LevelRefWrap {
  level: { value?: { data?: RawLevel }; data?: RawLevel };
  loyaltyactionId?: string | null;
}
export interface RawGoalData {
  id: string;
  title?: string;
  description?: string;
  image?: string;
  levels: LevelRefWrap[];
}
/** A `learning-center-module` entry: `{ id (contentID), data }`. */
export interface RawModuleEntry {
  id: string;
  data?: RawModule;
}

const moduleData = (m: ModuleRefWrap["module"]): RawModule =>
  m.value?.data ?? m.data ?? {};
const levelData = (l: LevelRefWrap["level"]): RawLevel | undefined =>
  l.value?.data ?? l.data;

/**
 * Unwrap a resolved Builder reference to its inlined entry. The server
 * tree-fetch resolves a `{ @type:"…Reference", id, model }` stub in place by
 * attaching the entry at `.value`; consumers that render the target (e.g.
 * `<Content>`) need that entry (`{ data: { blocks }}`), not the wrapper. Passes
 * non-references (and unresolved stubs) through unchanged.
 */
const unwrapRef = (ref: unknown): unknown => {
  const r = ref as { ["@type"]?: string; value?: unknown } | null | undefined;
  return r && r["@type"] === "@builder.io/core:Reference" && r.value
    ? r.value
    : ref;
};

// --- createLevels ---------------------------------------------------------

export interface TransformedModuleSummary {
  moduleID?: string;
  moduleNumber: number;
  title?: string;
  image?: string;
  estimatedCompletionTime?: number;
  allLessonsQty: number;
  completeLessonsQty: number;
  complete: boolean;
  started: boolean;
}
export interface TransformedLevel {
  levelID: string;
  levelNumber: number;
  title?: string;
  modules: TransformedModuleSummary[];
  started: boolean;
}

const getCompleteLessonsQty = (
  mod: RawModule,
  sfccModule: SfccModule | undefined,
): number => {
  const builderLessons = mod.lessons ?? [];
  const sfccLessons = sfccModule?.lessons ?? [];
  const isQuizComplete = !!sfccModule?.quiz?.complete;
  let qty = builderLessons.reduce((total, bl) => {
    const lessonID = bl.lesson?.value?.data?.id;
    return total + (sfccLessons.some(sl => sl.id === lessonID) ? 1 : 0);
  }, 0);
  if (isQuizComplete) qty++;
  return qty;
};

const buildModules = (
  level: RawLevel,
  sfccLevel: SfccLevel | undefined,
): TransformedModuleSummary[] =>
  level.modules.map((wrap, moduleIndex) => {
    const mod = moduleData(wrap.module);
    const sfccModule = (sfccLevel?.modules ?? []).find(m => m.id === mod.id);
    return {
      moduleID: mod.id,
      moduleNumber: moduleIndex + 1,
      title: mod.title,
      image: mod.image,
      estimatedCompletionTime: mod.estimatedCompletionTime,
      allLessonsQty: (mod.lessons?.length ?? 0) + 1, // +1 for the quiz
      completeLessonsQty: getCompleteLessonsQty(mod, sfccModule),
      complete: !!sfccModule?.complete,
      started: !!sfccModule?.start,
    };
  });

export function createLevels(
  goalData: RawGoalData | null | undefined,
  sfccGoal: SfccGoal | undefined,
): TransformedLevel[] {
  if (!goalData) return [];
  return goalData.levels.map((wrap, levelIndex) => {
    const level = levelData(wrap.level);
    const sfccLevel = sfccGoal?.levels.find(l => l.id === level?.id);
    return {
      levelID: level?.id ?? "",
      levelNumber: levelIndex + 1,
      title: level?.title,
      modules: level ? buildModules(level, sfccLevel) : [],
      started: !!sfccLevel?.start,
    };
  });
}

// --- createModule ---------------------------------------------------------

export interface TransformedModule {
  contentID: string;
  id: string;
  title: string;
  image: string;
  lessons: {
    id?: string;
    title?: string;
    content?: unknown;
    complete: boolean;
  }[];
  firstIncompleteLessonID: string | null;
  allLessonsComplete: boolean;
  quiz: {
    id?: string;
    complete: boolean;
    questions: {
      question: string;
      answers: string[];
      correctAnswerIndex: number;
    }[];
  } | null;
  summary: string | null;
  complete: boolean;
  shouldQuizButtonBeEnabled: boolean;
}

const getModuleTitle = (mod: RawModule | undefined): string => {
  if (!mod?.id) return "";
  const parts = mod.id.split("-");
  return `Module ${parts[parts.length - 1]}: ${mod.title ?? ""}`;
};

export function createModule(
  moduleEntry: RawModuleEntry,
  sfccModule: SfccModule | undefined,
): TransformedModule {
  const data = moduleEntry.data;
  const sfccLessons = sfccModule?.lessons ?? [];
  let firstIncompleteLessonID: string | null = null;
  let allLessonsComplete = true;

  const lessons = (data?.lessons ?? []).map(ref => {
    const ld = ref.lesson?.value?.data ?? {};
    const complete = sfccLessons.some(
      sl => !!sl.id && !!ld.id && sl.id === ld.id,
    );
    if (!complete) {
      allLessonsComplete = false;
      if (!firstIncompleteLessonID) firstIncompleteLessonID = ld.id ?? null;
    }
    return {
      id: ld.id,
      title: ld.title,
      // `content` references a `learning-center-lesson-content` entry; unwrap the
      // resolved reference so LessonContentPreview's `<Content>` gets the entry's
      // `data.blocks` and the lesson body renders (not a blank panel).
      content: unwrapRef(ld.content),
      complete,
    };
  });

  const shouldQuizButtonBeEnabled = (() => {
    if (lessons.length < 2) return false;
    const completed = lessons.filter(l => l.complete);
    const lastLesson = lessons[lessons.length - 1];
    return (
      completed.length === lessons.length ||
      (completed.length === lessons.length - 1 &&
        !!lastLesson &&
        !lastLesson.complete)
    );
  })();

  const getQuiz = (): TransformedModule["quiz"] => {
    const quizRef = data?.quiz;
    if (!quizRef) return null;
    const qd = quizRef.value?.data ?? {};
    const questions = (qd.questions ?? []).map(q => {
      let correctAnswerIndex = 0;
      const answers = q.answers.map((a, i) => {
        if (a.isCorrect) correctAnswerIndex = i;
        return a.answer;
      });
      return { question: q.question, answers, correctAnswerIndex };
    });
    return { id: qd.id, complete: !!sfccModule?.quiz?.complete, questions };
  };

  return {
    contentID: moduleEntry.id,
    id: data?.id ?? "",
    title: getModuleTitle(data),
    image: data?.image ?? "",
    lessons,
    firstIncompleteLessonID,
    allLessonsComplete,
    quiz: getQuiz(),
    summary: data?.summary ?? null,
    complete: !!sfccModule?.complete,
    shouldQuizButtonBeEnabled,
  };
}

// --- createGoalBanner -----------------------------------------------------

export interface GoalBannerLevel {
  loyaltyactionId: string | null;
  parentGoalID: string;
  levelID?: string;
  levelNumber: number;
  complete: boolean;
  started: boolean;
  allModulesQty: number;
  nextModuleID: string | null;
  nextModuleUrl: string;
  firstModuleUrl: string;
  completeModulesQty: number;
  percentComplete: number;
}
export interface GoalBanner {
  title?: string;
  description?: string;
  image?: string;
  levels: GoalBannerLevel[];
}

const modulePageUrl = (wrap: ModuleRefWrap | undefined): string =>
  moduleData(wrap?.module ?? {}).page?.value?.data?.url ?? "";

export function createGoalBanner(
  goalData: RawGoalData | null | undefined,
  sfccGoal: SfccGoal | undefined,
): GoalBanner {
  const sfccLevels = sfccGoal?.levels ?? [];
  const levels: GoalBannerLevel[] = (goalData?.levels ?? []).map(
    (wrap, levelIndex) => {
      const level = levelData(wrap.level);
      const sfccLevel = sfccLevels.find(l => l.id === level?.id);
      const sfccModules = sfccLevel?.modules ?? [];
      const completeModulesQty = sfccModules.reduce(
        (total, m) => total + (m?.complete ? 1 : 0),
        0,
      );
      const builderModules = level?.modules ?? [];
      const totalModules = builderModules.length;
      const nextModuleID = getNextIncompleteModuleID(level, sfccLevel);
      const nextModuleWrap = nextModuleID
        ? builderModules.find(
            ({ module }) => moduleData(module).id === nextModuleID,
          )
        : undefined;
      return {
        loyaltyactionId: wrap.loyaltyactionId ?? level?.loyaltyActionId ?? null,
        parentGoalID: goalData?.id ?? "",
        levelID: level?.id,
        levelNumber: levelIndex + 1,
        complete: totalModules === completeModulesQty,
        started: !!sfccLevel?.start,
        allModulesQty: totalModules,
        nextModuleID,
        nextModuleUrl: modulePageUrl(nextModuleWrap),
        firstModuleUrl: modulePageUrl(builderModules[0]),
        completeModulesQty,
        percentComplete:
          totalModules > 0 ? (completeModulesQty / totalModules) * 100 : 0,
      };
    },
  );
  return {
    title: goalData?.title,
    description: goalData?.description,
    image: goalData?.image,
    levels,
  };
}
